import { ReactNode, useState, useCallback } from "react";
import { Modal, ModalContextType } from "./type";
import { ModalContext } from "./context";
import { ModalRenderer } from "../../components/modals/modal_renderer";

const { Provider } = ModalContext;

export const ModalProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [modal, setModal] = useState<Modal | null>(null);

  const openModal = useCallback(
    (
      modalConfig: Omit<Modal, "currentStepIndex"> & {
        currentStepIndex?: number;
      }
    ): void => {
      const newModal: Modal = {
        ...modalConfig,
        currentStepIndex: modalConfig.currentStepIndex ?? 0,
      };

      setModal(newModal);
    },
    []
  );

  const closeModal = useCallback((): void => {
    setModal((prev) => {
      if (prev?.onClose) {
        prev.onClose();
      }
      return null;
    });
  }, []);

  const goToStep = useCallback((stepIndex: number): void => {
    setModal((prev) => {
      if (prev && stepIndex >= 0 && stepIndex < prev.steps.length) {
        return { ...prev, currentStepIndex: stepIndex };
      }
      return prev;
    });
  }, []);

  const goToNextStep = useCallback(async (): Promise<void> => {
    setModal((prev) => {
      if (!prev) return prev;

      const currentStep = prev.steps[prev.currentStepIndex];
      if (currentStep?.onNext) {
        const result = currentStep.onNext();
        if (result && typeof result.catch === "function") {
          result.catch((error: any) => {
            console.error("Error in modal step onNext:", error);
          });
        }
      }

      const nextIndex = prev.currentStepIndex + 1;
      if (nextIndex < prev.steps.length) {
        return { ...prev, currentStepIndex: nextIndex };
      }
      return prev;
    });
  }, []);

  const goToPreviousStep = useCallback(async (): Promise<void> => {
    setModal((prev) => {
      if (!prev) return prev;

      const currentStep = prev.steps[prev.currentStepIndex];
      if (currentStep?.onBack) {
        const result = currentStep.onBack();
        if (result && typeof result.catch === "function") {
          result.catch((error: any) => {
            console.error("Error in modal step onBack:", error);
          });
        }
      }

      const previousIndex = prev.currentStepIndex - 1;
      if (previousIndex >= 0) {
        return { ...prev, currentStepIndex: previousIndex };
      }
      return prev;
    });
  }, []);

  const value: ModalContextType = {
    modal,
    isOpen: modal !== null,
    openModal,
    closeModal,
    goToStep,
    goToNextStep,
    goToPreviousStep,
  };

  return (
    <Provider value={value}>
      {children}
      <ModalRenderer />
    </Provider>
  );
};
